import { useState, useEffect } from "react";
import {
  get_member_attendance_summary,
  get_staff_summary,
  getCurrentUserRoleService,
  getStudentLeavesService, // ✅ IMPORT ADDED
} from "services/api-service";
import { getHolidaysByMonth, getDateExceptionsService } from "services/api-service";

export default function AttendanceReportModal() {
  const today = new Date();
  const currentMonth = today.toISOString().split("T")[0].substring(0, 7);

  const [step, setStep] = useState("initial");
  const [reportType, setReportType] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(currentMonth);
  const [persons, setPersons] = useState([]);
  const [selectedPerson, setSelectedPerson] = useState(null);
  const [monthlyData, setMonthlyData] = useState(null);
  const [allMonthData, setAllMonthData] = useState({});
  const [loading, setLoading] = useState(false);
  const [hoveredDay, setHoveredDay] = useState(null);
  const [holidays, setHolidays] = useState({});
  const [userRole, setUserRole] = useState(null);
  const [dateExceptions, setDateExceptions] = useState({});
  const [studentLeaves, setStudentLeaves] = useState([]); // ✅ STATE FOR LEAVES ADDED

  // Fetch user role on mount
  useEffect(() => {
    getCurrentUserRoleService()
      .then((res) => setUserRole(res.data.role_type))
      .catch((err) => console.error("Error fetching user role:", err));
  }, []);

  const getYearMonth = (monthStr) => {
    const [year, month] = monthStr.split("-");
    return { year: parseInt(year), month: parseInt(month) };
  };

  const fetchHolidaysForMonth = async (monthStr) => {
    try {
      const { year, month } = getYearMonth(monthStr);
      const response = await getHolidaysByMonth(year, month);

      const holidayMap = {};
      if (response?.data?.holidays) {
        response.data.holidays.forEach((holiday) => {
          const dateStr = holiday.date;
          if (dateStr) {
            holidayMap[dateStr] = {
              name: holiday.name,
              category: holiday.category,
              description: holiday.description,
            };
          }
        });
      }

      setHolidays(holidayMap);
      return holidayMap;
    } catch (error) {
      console.error("Error fetching holidays:", error);
      setHolidays({});
      return {};
    }
  };

  const fetchDateExceptionsForMonth = async (monthStr) => {
    try {
      const { year, month } = getYearMonth(monthStr);
      const daysInMonth = new Date(year, month, 0).getDate();
      const exceptionsMap = {};

      const datesToFetch = [];
      for (let day = 1; day <= daysInMonth; day++) {
        const dateStr = `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
        datesToFetch.push(dateStr);
      }

      const results = await Promise.allSettled(
        datesToFetch.map((dateStr) => getDateExceptionsService(dateStr))
      );

      results.forEach((result, index) => {
        const dateStr = datesToFetch[index];

        if (result.status === "fulfilled" && result.value?.data) {
          const response = result.value.data;

          exceptionsMap[dateStr] = {
            studentsOnHoliday: response.students_on_holiday || [],
            staffOnHoliday: response.staff_on_holiday || [],
            closedSections: response.closed_sections || [],
          };
        }
      });

      setDateExceptions(exceptionsMap);
      return exceptionsMap;
    } catch (error) {
      console.error("Error fetching date exceptions:", error);
      setDateExceptions({});
      return {};
    }
  };

const fetchMonthAttendanceData = async (type, monthStr) => {
    try {
      const { year, month } = getYearMonth(monthStr);
      const daysInMonth = new Date(year, month, 0).getDate();
      
      // 1. Prepare all the dates we need to fetch
      const datesToFetch = [];
      for (let day = 1; day <= daysInMonth; day++) {
        datesToFetch.push(`${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`);
      }

      // 2. Fire ALL 30/31 requests at the exact same time concurrently
      const results = await Promise.allSettled(
        datesToFetch.map((formattedDate) => 
          type === "student"
            ? get_member_attendance_summary({
                date: formattedDate,
                page_number: 1,
                page_size: 5000,
              })
            : get_staff_summary({
                date: formattedDate,
                page_number: 1,
                page_size: 5000,
              })
        )
      );

      // 3. Gather the successful results
      const allRecords = [];
      results.forEach((result, index) => {
        if (result.status === "fulfilled" && result.value) {
          const data = result.value?.data?.data || result.value?.data || [];
          allRecords.push(...data);
        } else {
          console.error(`Error fetching data for ${datesToFetch[index]}:`, result.reason);
        }
      });

      return allRecords;
    } catch (error) {
      console.error("Error fetching month attendance data:", error);
      return [];
    }
  };

  const handleReportTypeSelect = async (type) => {
    setReportType(type);
    setLoading(true);

    try {
      await fetchHolidaysForMonth(selectedMonth);
      await fetchDateExceptionsForMonth(selectedMonth);

      // ✅ NEW: Fetch Approved Leaves
      if (type === "student") {
        try {
          const leavesRes = await getStudentLeavesService();
          setStudentLeaves(leavesRes.data.data || []);
        } catch (err) {
          console.error("Failed to load Member leaves:", err);
        }
      }

      const cacheKey = `${type}-${selectedMonth}`;
      let allRecords;

      if (allMonthData[cacheKey]) {
        allRecords = allMonthData[cacheKey];
      } else {
        allRecords = await fetchMonthAttendanceData(type, selectedMonth);
        setAllMonthData((prev) => ({ ...prev, [cacheKey]: allRecords }));
      }

      const uniquePersonsMap = new Map();

      if (type === "student") {
        allRecords.forEach((record) => {
          if (record.member_id && !uniquePersonsMap.has(record.member_id)) {
            uniquePersonsMap.set(record.member_id, {
              id: record.member_id,
              name: record.member_name || "Unknown",
            });
          }
        });
      } else {
        allRecords.forEach((record) => {
          if (record.staff_id && !uniquePersonsMap.has(record.staff_id)) {
            uniquePersonsMap.set(record.staff_id, {
              id: record.staff_id,
              name: record.staff_name || "Unknown",
            });
          }
        });
      }

      const uniquePersons = Array.from(uniquePersonsMap.values());

      if (uniquePersons.length === 0) {
        alert(`No ${type}s found for ${selectedMonth}`);
        setLoading(false);
        return;
      }

      setPersons(uniquePersons.sort((a, b) => a.name.localeCompare(b.name)));
      setStep("selectPerson");
    } catch (error) {
      console.error("Error:", error);
      alert("Failed to load " + type + " list");
    } finally {
      setLoading(false);
    }
  };

  const handlePersonSelect = (person) => {
    setSelectedPerson(person);
    setStep("monthlyReport");
    generateMonthlyReport(person);
  };

  const generateMonthlyReport = async (person) => {
    setLoading(true);
    try {
      const { year, month } = getYearMonth(selectedMonth);
      const daysInMonth = new Date(year, month, 0).getDate();

      const today = new Date();
      const daysUpToToday =
        today.getFullYear() === year && today.getMonth() + 1 === month
          ? today.getDate()
          : daysInMonth;

      const cacheKey = `${reportType}-${selectedMonth}`;
      const allRecords = allMonthData[cacheKey];

      if (!allRecords) {
        alert("Data not found. Please go back and select again.");
        return;
      }

      let present = 0;
      let absent = 0;
      const dailyRecords = {};

      const personRecords = allRecords.filter((r) => {
        const idMatch =
          reportType === "student"
            ? String(r.member_id) === String(person.id)
            : String(r.staff_id) === String(person.id);

        const dateStr = r.date || r.attendance_date || r.created_at;
        const isCorrectMonth = dateStr && dateStr.startsWith(selectedMonth);

        return idMatch && isCorrectMonth;
      });

      const recordsByDay = {};
      personRecords.forEach((record) => {
        const dateStr = record.date || record.attendance_date || record.created_at;
        if (!dateStr) return;

        let day;
        try {
          const parts = dateStr.split("T")[0].split("-");
          day = parseInt(parts[2]);
        } catch (e) {
          return;
        }
        recordsByDay[day] = record;
      });

      for (let day = 1; day <= daysUpToToday; day++) {
        const dateStr = `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
        const isHoliday = !!holidays[dateStr];
        const record = recordsByDay[day];

        let isStudentOnHoliday = false;
        let isStaffOnHoliday = false;
        let leaveReason = ""; // ✅ Track reason

        // Check standard date exceptions
        if (dateExceptions[dateStr]) {
          if (reportType === "student") {
            isStudentOnHoliday = dateExceptions[dateStr].studentsOnHoliday?.includes(
              String(person.id)
            );
          } else if (reportType === "staff") {
            isStaffOnHoliday = dateExceptions[dateStr].staffOnHoliday?.includes(
              String(person.id)
            );
          }
        }

        // ✅ NEW: Check the Leave Management Database
        if (reportType === "student") {
          const matchingLeave = studentLeaves.find((leave) => {
            return (
              String(leave.student_id) === String(person.id) &&
              dateStr >= leave.start_date &&
              dateStr <= leave.end_date
            );
          });
          if (matchingLeave) {
            isStudentOnHoliday = true; 
            leaveReason = matchingLeave.reason;
          }
        }

        if (isHoliday) {
          dailyRecords[day] = {
            status: "Holiday",
            time: null,
            checkIn: null,
            checkOut: null,
            isHoliday: true,
            holidayName: holidays[dateStr]?.name,
            holidayCategory: holidays[dateStr]?.category,
          };
        } else if (isStudentOnHoliday) {
          dailyRecords[day] = {
            status: "Student Holiday",
            time: null,
            checkIn: null,
            checkOut: null,
            isStudentOnHoliday: true,
            // ✅ Dynamically show the actual leave reason!
            holidayName: leaveReason ? `Leave: ${leaveReason}` : "Member on Leave/Holiday",
          };
        } else if (isStaffOnHoliday) {
          dailyRecords[day] = {
            status: "Staff Holiday",
            time: null,
            checkIn: null,
            checkOut: null,
            isStaffOnHoliday: true,
            holidayName: "Staff on Leave/Holiday",
          };
        } else if (record) {
          dailyRecords[day] = {
            status: record.status,
            time:
              reportType === "student"
                ? record.check_in_time || record.recognition_time
                : record.check_in_time || record.check_out_time,
            checkIn: record.check_in_time,
            checkOut: record.check_out_time,
            isHoliday: false,
            isStudentOnHoliday: false,
            isStaffOnHoliday: false,
          };

          if (record.status === "Present") {
            present++;
          } else if (record.status === "Absent") {
            absent++;
          }
        } else {
          dailyRecords[day] = {
            status: "No Data",
            time: null,
            checkIn: null,
            checkOut: null,
            isHoliday: false,
            isStudentOnHoliday: false,
            isStaffOnHoliday: false,
          };
        }
      }

      setMonthlyData({
        present,
        absent,
        totalDays: daysUpToToday,
        daysInMonth,
        daysUpToToday,
        percentage: daysUpToToday > 0 ? Math.round((present / daysUpToToday) * 100) : 0,
        dailyRecords,
      });
    } catch (error) {
      console.error("Error generating report:", error);
      alert("Failed to generate report");
    } finally {
      setLoading(false);
    }
  };

  const downloadReportAsExcel = async () => {
    if (!monthlyData || !selectedPerson) return;

    try {
      const XLSX = await import("xlsx");
      const { year, month } = getYearMonth(selectedMonth);

      const wsData = [];

      wsData.push(["ATTENDANCE REPORT"]);
      wsData.push([]);
      wsData.push(["NAME:", selectedPerson.name]);
      wsData.push(["ID:", selectedPerson.id]);
      wsData.push(["TYPE:", reportType === "student" ? "Member" : "Staff"]);
      wsData.push(["MONTH:", selectedMonth]);
      wsData.push([]);
      wsData.push([]);

      const headers = ["Day", "Date", "Status", "In Time", "Out Time", "Notes"];
      wsData.push(headers);

      for (let day = 1; day <= monthlyData.daysUpToToday; day++) {
        const record = monthlyData.dailyRecords[day];
        const dateStr = `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;

        let status, notes;
        if (record?.isHoliday) {
          status = "H";
          notes = record.holidayName ? `Holiday: ${record.holidayName}` : "Holiday";
        } else if (record?.isStudentOnHoliday) {
          status = "Leave";
          notes = record.holidayName || "Student on Leave"; // ✅ Inject reason
        } else if (record?.isStaffOnHoliday) {
          status = "Leave";
          notes = "Staff on Leave/Holiday";
        } else {
          status =
            record?.status === "Present" ? "P" : record?.status === "Absent" ? "A" : "-";
          notes = "";
        }

        const inTime =
          record?.checkIn &&
          !record?.isHoliday &&
          !record?.isStudentOnHoliday &&
          !record?.isStaffOnHoliday
            ? formatTime(record.checkIn)
            : "-";
        const outTime =
          record?.checkOut &&
          !record?.isHoliday &&
          !record?.isStudentOnHoliday &&
          !record?.isStaffOnHoliday
            ? formatTime(record.checkOut)
            : "-";

        wsData.push([day, dateStr, status, inTime, outTime, notes]);
      }

      wsData.push([]);
      wsData.push(["SUMMARY"]);
      wsData.push(["Total Days (excl. holidays):", monthlyData.daysUpToToday]);
      wsData.push(["Present (P):", monthlyData.present]);
      wsData.push(["Absent (A):", monthlyData.absent]);
      wsData.push(["Attendance %:", `${monthlyData.percentage}%`]);

      const worksheet = XLSX.utils.aoa_to_sheet(wsData);

      worksheet["!cols"] = [
        { wch: 8 },
        { wch: 12 },
        { wch: 12 },
        { wch: 15 },
        { wch: 15 },
        { wch: 30 },
      ];

      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Attendance");
      XLSX.writeFile(
        workbook,
        `${reportType}_Attendance_${selectedPerson.id}_${selectedMonth}.xlsx`
      );
    } catch (error) {
      console.error("Error downloading Excel:", error);
      alert("Failed to download Excel file");
    }
  };

  const downloadReportAsCSV = () => {
    if (!monthlyData || !selectedPerson) return;

    const { year, month } = getYearMonth(selectedMonth);
    let csvContent = "ATTENDANCE REPORT\n\n";
    csvContent += `NAME,${selectedPerson.name}\n`;
    csvContent += `ID,${selectedPerson.id}\n`;
    csvContent += `TYPE,${reportType === "student" ? "Member" : "Staff"}\n`;
    csvContent += `MONTH,${selectedMonth}\n\n`;

    csvContent += "Day,Date,Status,In Time,Out Time,Notes\n";

    for (let day = 1; day <= monthlyData.daysUpToToday; day++) {
      const record = monthlyData.dailyRecords[day];
      const dateStr = `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;

      let status, notes;
      if (record?.isHoliday) {
        status = "H";
        notes = record.holidayName ? `Holiday: ${record.holidayName}` : "Holiday";
      } else if (record?.isStudentOnHoliday) {
        status = "Leave";
        notes = record.holidayName || "Student on Leave"; // ✅ Inject reason
      } else if (record?.isStaffOnHoliday) {
        status = "Leave";
        notes = "Staff on Leave/Holiday";
      } else {
        status =
          record?.status === "Present" ? "P" : record?.status === "Absent" ? "A" : "-";
        notes = "";
      }

      const inTime =
        record?.checkIn &&
        !record?.isHoliday &&
        !record?.isStudentOnHoliday &&
        !record?.isStaffOnHoliday
          ? formatTime(record.checkIn)
          : "-";
      const outTime =
        record?.checkOut &&
        !record?.isHoliday &&
        !record?.isStudentOnHoliday &&
        !record?.isStaffOnHoliday
          ? formatTime(record.checkOut)
          : "-";

      csvContent += `${day},${dateStr},${status},${inTime},${outTime},"${notes}"\n`;
    }

    csvContent += "\nSUMMARY\n";
    csvContent += `Total Days (excl. holidays),${monthlyData.daysUpToToday}\n`;
    csvContent += `Present,${monthlyData.present}\n`;
    csvContent += `Absent,${monthlyData.absent}\n`;
    csvContent += `Attendance %,${monthlyData.percentage}%\n`;

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${reportType}_Attendance_${selectedPerson.id}_${selectedMonth}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const downloadReportAsPDF = () => {
    if (!monthlyData || !selectedPerson) return;

    const { year, month } = getYearMonth(selectedMonth);
    let htmlContent = `
      <html>
        <head>
          <style>
            body { font-family: Arial, sans-serif; margin: 20px; color: #333; }
            .header { text-align: center; margin-bottom: 30px; border-bottom: 3px solid #4f46e5; padding-bottom: 15px; }
            .header h1 { color: #4f46e5; margin: 0; font-size: 24px; }
            .info-section { margin-bottom: 20px; }
            .info-row { display: flex; justify-content: space-between; padding: 8px 0; border-bottom: 1px solid #e5e7eb; }
            .info-label { font-weight: 600; color: #374151; }
            .info-value { color: #1f2937; }
            table { width: 100%; border-collapse: collapse; margin: 20px 0; }
            th { background-color: #4f46e5; color: white; padding: 12px; text-align: left; font-weight: 600; }
            td { padding: 10px 12px; border-bottom: 1px solid #e5e7eb; }
            tr:nth-child(even) { background-color: #f9fafb; }
            .status-p { color: #10b981; font-weight: 600; }
            .status-a { color: #ef4444; font-weight: 600; }
            .status-h { color: #f59e0b; font-weight: 600; background-color: #fffbeb; padding: 2px 6px; border-radius: 4px; }
            .status-leave { color: #8b5cf6; font-weight: 600; background-color: #faf5ff; padding: 2px 6px; border-radius: 4px; }
            .summary { margin-top: 30px; background-color: #f3f4f6; padding: 20px; border-radius: 8px; }
            .summary-row { display: flex; justify-content: space-between; padding: 10px 0; border-bottom: 1px solid #d1d5db; }
            .summary-row:last-child { border-bottom: none; }
            .summary-label { font-weight: 600; }
            .summary-value { font-weight: 700; color: #4f46e5; }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>ATTENDANCE REPORT</h1>
          </div>

          <div class="info-section">
            <div class="info-row">
              <span class="info-label">Name:</span>
              <span class="info-value">${selectedPerson.name}</span>
            </div>
            <div class="info-row">
              <span class="info-label">ID:</span>
              <span class="info-value">${selectedPerson.id}</span>
            </div>
            <div class="info-row">
              <span class="info-label">Type:</span>
              <span class="info-value">${reportType === "student" ? "Member" : "Staff"}</span>
            </div>
            <div class="info-row">
              <span class="info-label">Month:</span>
              <span class="info-value">${selectedMonth}</span>
            </div>
          </div>

          <table>
            <thead>
              <tr>
                <th style="width: 10%;">Day</th>
                <th style="width: 20%;">Date</th>
                <th style="width: 15%;">Status</th>
                <th style="width: 30%;">In Time</th>
                <th style="width: 25%;">Notes</th>
              </tr>
            </thead>
            <tbody>
    `;

    for (let day = 1; day <= monthlyData.daysUpToToday; day++) {
      const record = monthlyData.dailyRecords[day];
      const dateStr = `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;

      let status, statusClass, notes;
      if (record?.isHoliday) {
        status = "H";
        statusClass = "status-h";
        notes = record.holidayName ? `Holiday: ${record.holidayName}` : "Holiday";
      } else if (record?.isStudentOnHoliday) {
        status = "🏖️ Leave";
        statusClass = "status-leave";
        notes = record.holidayName || "Student on Leave"; // ✅ Inject reason
      } else if (record?.isStaffOnHoliday) {
        status = "🏖️ Leave";
        statusClass = "status-leave";
        notes = "Staff on Leave/Holiday";
      } else {
        status =
          record?.status === "Present" ? "P" : record?.status === "Absent" ? "A" : "-";
        statusClass =
          status === "P" ? "status-p" : status === "A" ? "status-a" : "";
        notes = "";
      }

      const inTime =
        record?.checkIn &&
        !record?.isHoliday &&
        !record?.isStudentOnHoliday &&
        !record?.isStaffOnHoliday
          ? formatTime(record.checkIn)
          : "—";

      htmlContent += `
        <tr>
          <td>${day}</td>
          <td>${dateStr}</td>
          <td class="${statusClass}">${status}</td>
          <td>${inTime}</td>
          <td>${notes}</td>
        </tr>
      `;
    }

    htmlContent += `
            </tbody>
          </table>

          <div class="summary">
            <h3 style="margin-top: 0; color: #4f46e5;">Summary</h3>
            <div class="summary-row">
              <span class="summary-label">Total Days (excl. holidays):</span>
              <span class="summary-value">${monthlyData.daysUpToToday}</span>
            </div>
            <div class="summary-row">
              <span class="summary-label">Present (P):</span>
              <span class="summary-value" style="color: #10b981;">${monthlyData.present}</span>
            </div>
            <div class="summary-row">
              <span class="summary-label">Absent (A):</span>
              <span class="summary-value" style="color: #ef4444;">${monthlyData.absent}</span>
            </div>
            <div class="summary-row">
              <span class="summary-label">Attendance %:</span>
              <span class="summary-value">${monthlyData.percentage}%</span>
            </div>
          </div>
        </body>
      </html>
    `;

    const newWindow = window.open();
    newWindow.document.write(htmlContent);
    newWindow.document.close();
    newWindow.print();
  };

  const formatTime = (timeString) => {
    if (!timeString) return "—";
    if (timeString.includes("T")) {
      return timeString.split("T")[1].substring(0, 5);
    }
    return timeString.substring(0, 5);
  };

  const handleBack = () => {
    if (step === "monthlyReport") {
      setStep("selectPerson");
      setSelectedPerson(null);
      setMonthlyData(null);
    } else if (step === "selectPerson") {
      setStep("initial");
      setReportType(null);
      setPersons([]);
    }
  };

  // ─────────────────────────────────────────────
  // RENDER: initial step
  // ─────────────────────────────────────────────
  if (step === "initial") {
    return (
      <div style={{ padding: "20px" }}>
        <div style={{ maxWidth: "600px", margin: "0 auto" }}>
          <h1
            style={{
              fontSize: "24px",
              fontWeight: "600",
              color: "#1a1a1a",
              marginBottom: "12px",
            }}
          >
            Attendance Report
          </h1>
          <p style={{ color: "#666", fontSize: "14px", marginBottom: "20px" }}>
            Generate monthly attendance reports for Members and staff. Holidays and leaves are
            marked and clearly shown.
          </p>

          <div
            style={{
              marginBottom: "24px",
              backgroundColor: "white",
              padding: "16px",
              borderRadius: "8px",
              border: "1px solid #e5e7eb",
            }}
          >
            <label
              style={{ display: "block", marginBottom: "8px", fontWeight: "600", color: "#374151" }}
            >
              Select Month:
            </label>
            <input
              type="month"
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(e.target.value)}
              style={{
                padding: "10px 12px",
                borderRadius: "6px",
                border: "1px solid #d0d0d0",
                fontSize: "14px",
                width: "100%",
              }}
            />
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: userRole === "IT_ADMIN" ? "1fr 1fr" : "1fr",
              gap: "12px",
            }}
          >
            <button
              onClick={() => handleReportTypeSelect("student")}
              disabled={loading}
              style={{
                padding: "16px",
                background: loading ? "#ccc" : "white",
                border: "2px solid #e5e7eb",
                borderRadius: "8px",
                cursor: loading ? "not-allowed" : "pointer",
                textAlign: "center",
              }}
              onMouseOver={(e) => {
                if (!loading) {
                  e.currentTarget.style.borderColor = "#4f46e5";
                  e.currentTarget.style.backgroundColor = "#f0f9ff";
                }
              }}
              onMouseOut={(e) => {
                if (!loading) {
                  e.currentTarget.style.borderColor = "#e5e7eb";
                  e.currentTarget.style.backgroundColor = "white";
                }
              }}
            >
              <div style={{ fontSize: "24px", marginBottom: "8px" }}>📚</div>
              <div style={{ fontWeight: "600", color: "#1a1a1a", fontSize: "15px" }}>
                Member Report
              </div>
              {loading && (
                <div style={{ fontSize: "12px", color: "#666", marginTop: "8px" }}>
                  Loading...
                </div>
              )}
            </button>

            {userRole === "IT_ADMIN" && (
              <button
                onClick={() => handleReportTypeSelect("staff")}
                disabled={loading}
                style={{
                  padding: "16px",
                  background: loading ? "#ccc" : "white",
                  border: "2px solid #e5e7eb",
                  borderRadius: "8px",
                  cursor: loading ? "not-allowed" : "pointer",
                  textAlign: "center",
                }}
                onMouseOver={(e) => {
                  if (!loading) {
                    e.currentTarget.style.borderColor = "#4f46e5";
                    e.currentTarget.style.backgroundColor = "#f0f9ff";
                  }
                }}
                onMouseOut={(e) => {
                  if (!loading) {
                    e.currentTarget.style.borderColor = "#e5e7eb";
                    e.currentTarget.style.backgroundColor = "white";
                  }
                }}
              >
                <div style={{ fontSize: "24px", marginBottom: "8px" }}>👔</div>
                <div style={{ fontWeight: "600", color: "#1a1a1a", fontSize: "15px" }}>
                  Staff Report
                </div>
                {loading && (
                  <div style={{ fontSize: "12px", color: "#666", marginTop: "8px" }}>
                    Loading...
                  </div>
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ─────────────────────────────────────────────
  // RENDER: select person step
  // ─────────────────────────────────────────────
  if (step === "selectPerson") {
    return (
      <div style={{ padding: "20px" }}>
        <div style={{ maxWidth: "800px", margin: "0 auto" }}>
          <button
            onClick={handleBack}
            style={{
              padding: "6px 12px",
              background: "#4f46e5",
              color: "white",
              border: "none",
              borderRadius: "4px",
              cursor: "pointer",
              fontWeight: "500",
              fontSize: "13px",
              marginBottom: "16px",
            }}
          >
            ← Back
          </button>

          <h1
            style={{
              fontSize: "24px",
              fontWeight: "600",
              color: "#1a1a1a",
              marginBottom: "12px",
            }}
          >
            Select {reportType === "student" ? "Member" : "Staff"} — {selectedMonth}
          </h1>

          {loading ? (
            <div style={{ textAlign: "center", padding: "40px", color: "#666" }}>
              Loading {reportType === "student" ? "members" : "staff"}...
            </div>
          ) : (
            <div
              style={{
                backgroundColor: "white",
                borderRadius: "8px",
                boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
                overflow: "hidden",
              }}
            >
              <div style={{ overflowY: "auto", maxHeight: "600px" }}>
                {persons.length === 0 ? (
                  <div style={{ padding: "40px", textAlign: "center", color: "#999" }}>
                    No {reportType === "student" ? "Member" : "staff"} found for{" "}
                    {selectedMonth}.
                  </div>
                ) : (
                  persons.map((person, idx) => (
                    <button
                      key={person.id}
                      onClick={() => handlePersonSelect(person)}
                      style={{
                        width: "100%",
                        padding: "12px 16px",
                        textAlign: "left",
                        border: "none",
                        borderBottom:
                          idx < persons.length - 1 ? "1px solid #e5e7eb" : "none",
                        backgroundColor: idx % 2 === 0 ? "#ffffff" : "#f9fafb",
                        cursor: "pointer",
                      }}
                      onMouseOver={(e) => {
                        e.currentTarget.style.backgroundColor = "#eff6ff";
                      }}
                      onMouseOut={(e) => {
                        e.currentTarget.style.backgroundColor =
                          idx % 2 === 0 ? "#ffffff" : "#f9fafb";
                      }}
                    >
                      <div
                        style={{ fontWeight: "600", color: "#1a1a1a", fontSize: "15px" }}
                      >
                        {person.name}
                      </div>
                      <div style={{ color: "#666", fontSize: "13px", marginTop: "4px" }}>
                        ID: {person.id}
                      </div>
                    </button>
                  ))
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  // ─────────────────────────────────────────────
  // RENDER: monthly report step
  // ─────────────────────────────────────────────
  if (step === "monthlyReport" && monthlyData) {
    return (
      <div
        style={{ padding: "20px", backgroundColor: "#f8f9fa", minHeight: "100vh" }}
      >
        <div style={{ maxWidth: "1000px", margin: "0 auto" }}>
          <button
            onClick={handleBack}
            style={{
              padding: "8px 16px",
              background: "#4f46e5",
              color: "white",
              border: "none",
              borderRadius: "6px",
              cursor: "pointer",
              fontWeight: "500",
              fontSize: "14px",
              marginBottom: "20px",
            }}
          >
            ← Back
          </button>

          {!loading && (
            <>
              <div
                style={{
                  backgroundColor: "white",
                  padding: "24px",
                  borderRadius: "12px",
                  marginBottom: "24px",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                    flexWrap: "wrap",
                    gap: "16px",
                  }}
                >
                  <div>
                    <h1
                      style={{
                        fontSize: "28px",
                        fontWeight: "700",
                        color: "#1a1a1a",
                        margin: "0 0 8px 0",
                      }}
                    >
                      {reportType === "student" ? "👔" : "👔"} {selectedPerson?.name}
                    </h1>
                    <p style={{ color: "#666", fontSize: "14px", margin: 0 }}>
                      ID: <strong>{selectedPerson?.id}</strong> • Type:{" "}
                      <strong>{reportType === "student" ? "Member" : "Staff"}</strong>
                    </p>
                  </div>
                  <div
                    style={{
                      textAlign: "right",
                      padding: "8px 16px",
                      backgroundColor: "#f0f9ff",
                      borderRadius: "8px",
                      border: "2px solid #4f46e5",
                    }}
                  >
                    <div style={{ fontSize: "12px", color: "#666" }}>Month:</div>
                    <div
                      style={{
                        fontSize: "18px",
                        fontWeight: "700",
                        color: "#4f46e5",
                      }}
                    >
                      {selectedMonth}
                    </div>
                  </div>
                </div>
              </div>

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
                  gap: "16px",
                  marginBottom: "24px",
                }}
              >
                <div
                  style={{
                    backgroundColor: "#ecfdf5",
                    border: "2px solid #10b981",
                    padding: "20px",
                    borderRadius: "12px",
                  }}
                >
                  <div
                    style={{
                      fontSize: "12px",
                      color: "#666",
                      marginBottom: "8px",
                      fontWeight: "600",
                    }}
                  >
                    PRESENT
                  </div>
                  <div
                    style={{ fontSize: "36px", fontWeight: "700", color: "#10b981" }}
                  >
                    {monthlyData.present}
                  </div>
                </div>

                <div
                  style={{
                    backgroundColor: "#fef2f2",
                    border: "2px solid #ef4444",
                    padding: "20px",
                    borderRadius: "12px",
                  }}
                >
                  <div
                    style={{
                      fontSize: "12px",
                      color: "#666",
                      marginBottom: "8px",
                      fontWeight: "600",
                    }}
                  >
                    ABSENT
                  </div>
                  <div
                    style={{ fontSize: "36px", fontWeight: "700", color: "#ef4444" }}
                  >
                    {monthlyData.absent}
                  </div>
                </div>

                <div
                  style={{
                    backgroundColor: "#fffbeb",
                    border: "2px solid #f59e0b",
                    padding: "20px",
                    borderRadius: "12px",
                  }}
                >
                  <div
                    style={{
                      fontSize: "12px",
                      color: "#666",
                      marginBottom: "8px",
                      fontWeight: "600",
                    }}
                  >
                    ATTENDANCE %
                  </div>
                  <div
                    style={{ fontSize: "36px", fontWeight: "700", color: "#f59e0b" }}
                  >
                    {monthlyData.percentage}%
                  </div>
                </div>

                <div
                  style={{
                    backgroundColor: "#f3f4f6",
                    border: "2px solid #6b7280",
                    padding: "20px",
                    borderRadius: "12px",
                  }}
                >
                  <div
                    style={{
                      fontSize: "12px",
                      color: "#666",
                      marginBottom: "8px",
                      fontWeight: "600",
                    }}
                  >
                    TOTAL DAYS
                  </div>
                  <div
                    style={{ fontSize: "36px", fontWeight: "700", color: "#374151" }}
                  >
                    {monthlyData.daysUpToToday}
                  </div>
                </div>
              </div>

              <div
                style={{
                  display: "flex",
                  gap: "12px",
                  marginBottom: "24px",
                  flexWrap: "wrap",
                }}
              >
                <button
                  onClick={downloadReportAsExcel}
                  style={{
                    padding: "12px 24px",
                    backgroundColor: "#10b981",
                    color: "white",
                    border: "none",
                    borderRadius: "8px",
                    cursor: "pointer",
                    fontWeight: "600",
                    fontSize: "14px",
                  }}
                >
                  📊 Download Excel
                </button>

                <button
                  onClick={downloadReportAsCSV}
                  style={{
                    padding: "12px 24px",
                    backgroundColor: "#f59e0b",
                    color: "white",
                    border: "none",
                    borderRadius: "8px",
                    cursor: "pointer",
                    fontWeight: "600",
                    fontSize: "14px",
                  }}
                >
                  📄 Download CSV
                </button>

                <button
                  onClick={downloadReportAsPDF}
                  style={{
                    padding: "12px 24px",
                    backgroundColor: "#ef4444",
                    color: "white",
                    border: "none",
                    borderRadius: "8px",
                    cursor: "pointer",
                    fontWeight: "600",
                    fontSize: "14px",
                  }}
                >
                  🖨️ Print PDF
                </button>
              </div>

              <div
                style={{
                  backgroundColor: "white",
                  borderRadius: "12px",
                  padding: "24px",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                  marginBottom: "24px",
                }}
              >
                <h2
                  style={{
                    fontSize: "18px",
                    fontWeight: "700",
                    color: "#1a1a1a",
                    marginBottom: "16px",
                  }}
                >
                  📅 Daily Breakdown
                </h2>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fill, minmax(100px, 1fr))",
                    gap: "16px",
                  }}
                >
                  {Array.from({ length: monthlyData.daysUpToToday }).map((_, idx) => {
                    const day = idx + 1;
                    const record = monthlyData.dailyRecords[day];
                    const status = record?.status;
                    const time = record?.time || record?.checkIn;

                    let bgColor, borderColor, textColor, icon;
                    if (record?.isHoliday) {
                      bgColor = "#fef3c7";
                      borderColor = "#f59e0b";
                      textColor = "#f59e0b";
                      icon = "🎉";
                    } else if (record?.isStudentOnHoliday) {
                      bgColor = "#faf5ff";
                      borderColor = "#8b5cf6";
                      textColor = "#8b5cf6";
                      icon = "🏖️";
                    } else if (record?.isStaffOnHoliday) {
                      bgColor = "#faf5ff";
                      borderColor = "#8b5cf6";
                      textColor = "#8b5cf6";
                      icon = "🏖️";
                    } else if (status === "Present") {
                      bgColor = "#ecfdf5";
                      borderColor = "#10b981";
                      textColor = "#10b981";
                      icon = "✓";
                    } else if (status === "Absent") {
                      bgColor = "#fef2f2";
                      borderColor = "#ef4444";
                      textColor = "#ef4444";
                      icon = "✗";
                    } else {
                      bgColor = "#f3f4f6";
                      borderColor = "#d1d5db";
                      textColor = "#6b7280";
                      icon = "—";
                    }

                    return (
                      <div
                        key={day}
                        onMouseEnter={() => setHoveredDay(day)}
                        onMouseLeave={() => setHoveredDay(null)}
                        style={{ position: "relative" }}
                      >
                        <div
                          style={{
                            padding: "16px 12px",
                            borderRadius: "8px",
                            backgroundColor: bgColor,
                            border: `2px solid ${borderColor}`,
                            color: textColor,
                            textAlign: "center",
                            cursor: "pointer",
                            transition: "all 0.2s",
                            boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
                            minHeight: "90px",
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "space-between",
                          }}
                          onMouseOver={(e) => {
                            e.currentTarget.style.transform =
                              "translateY(-4px) scale(1.05)";
                            e.currentTarget.style.boxShadow =
                              "0 8px 16px rgba(0,0,0,0.15)";
                          }}
                          onMouseOut={(e) => {
                            e.currentTarget.style.transform =
                              "translateY(0) scale(1)";
                            e.currentTarget.style.boxShadow =
                              "0 1px 3px rgba(0,0,0,0.05)";
                          }}
                        >
                          <div
                            style={{
                              fontSize: "24px",
                              fontWeight: "700",
                              lineHeight: "1",
                            }}
                          >
                            {day}
                          </div>
                          <div
                            style={{
                              fontSize: "32px",
                              fontWeight: "700",
                              lineHeight: "1",
                              margin: "8px 0",
                            }}
                          >
                            {icon}
                          </div>
                          <div
                            style={{
                              fontSize: "11px",
                              fontWeight: "600",
                              opacity: 0.8,
                              textTransform: "uppercase",
                              letterSpacing: "0.5px",
                            }}
                          >
                            {record?.isHoliday
                              ? "Holiday"
                              : record?.isStudentOnHoliday || record?.isStaffOnHoliday
                              ? "Leave"
                              : status === "Present"
                              ? "Present"
                              : status === "Absent"
                              ? "Absent"
                              : "No Data"}
                          </div>
                        </div>

                        {/* ✅ Tooltip Update: Show Reason! */}
                        {hoveredDay === day && (
                          <div
                            style={{
                              position: "absolute",
                              bottom: "100%",
                              left: "50%",
                              transform: "translateX(-50%)",
                              backgroundColor: "#1f2937",
                              color: "white",
                              padding: "12px 16px",
                              borderRadius: "8px",
                              fontSize: "13px",
                              fontWeight: "600",
                              whiteSpace: "nowrap",
                              zIndex: 1000,
                              marginBottom: "12px",
                              boxShadow: "0 4px 12px rgba(0,0,0,0.25)",
                              border: "1px solid rgba(255,255,255,0.1)",
                              animation: "fadeInUp 0.2s ease-out",
                            }}
                          >
                            {record?.isHoliday && (
                              <>
                                <div style={{ fontWeight: "700", marginBottom: "4px" }}>
                                  🎉 {record.holidayName || "Holiday"}
                                </div>
                                {record.holidayCategory && (
                                  <div
                                    style={{
                                      fontSize: "12px",
                                      opacity: 0.9,
                                      marginTop: "4px",
                                    }}
                                  >
                                    Category: {record.holidayCategory}
                                  </div>
                                )}
                              </>
                            )}
                            {record?.isStudentOnHoliday && (
                              <div style={{ fontWeight: "700" }}>
                                🏖️ {record.holidayName || "Student on Leave"}
                              </div>
                            )}
                            {record?.isStaffOnHoliday && (
                              <div style={{ fontWeight: "700" }}>
                                🏖️ Staff on Leave
                              </div>
                            )}
                            {!record?.isHoliday &&
                              !record?.isStudentOnHoliday &&
                              !record?.isStaffOnHoliday &&
                              status === "Present" && (
                                <>
                                  <div
                                    style={{ fontWeight: "700", marginBottom: "4px" }}
                                  >
                                    ✓ Present
                                  </div>
                                  {time && (
                                    <div
                                      style={{
                                        fontSize: "12px",
                                        opacity: 0.9,
                                        marginTop: "4px",
                                      }}
                                    >
                                      🕐 {formatTime(time)}
                                    </div>
                                  )}
                                  {record?.checkOut && reportType !== "student" && (
                                    <div
                                      style={{
                                        fontSize: "12px",
                                        opacity: 0.9,
                                        marginTop: "2px",
                                      }}
                                    >
                                      🚪 Out: {formatTime(record.checkOut)}
                                    </div>
                                  )}
                                </>
                              )}
                            {!record?.isHoliday &&
                              !record?.isStudentOnHoliday &&
                              !record?.isStaffOnHoliday &&
                              status === "Absent" && (
                                <div style={{ fontWeight: "700" }}>✗ Absent</div>
                              )}
                            {!record?.isHoliday &&
                              !record?.isStudentOnHoliday &&
                              !record?.isStaffOnHoliday &&
                              status === "No Data" && (
                                <div style={{ fontWeight: "700" }}>— No Data</div>
                              )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              <style>{`
                @keyframes fadeInUp {
                  from {
                    opacity: 0;
                    transform: translateX(-50%) translateY(10px);
                  }
                  to {
                    opacity: 1;
                    transform: translateX(-50%) translateY(0);
                  }
                }
              `}</style>
            </>
          )}
        </div>
      </div>
    );
  }

  return null;
}
